import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  Plus,
  LayoutGrid,
  List,
  Trash2,
  Filter,
  SortAsc,
  Eye,
  ChevronRight,
} from 'lucide-react';

const GROUP_OPTIONS = [
  { id: 'none', label: 'No grouping' },
  { id: 'state', label: 'State' },
  { id: 'priority', label: 'Priority' },
  { id: 'assignee', label: 'Assignee' },
  { id: 'cycle', label: 'Cycle' },
];

const SORT_OPTIONS = [
  { id: 'created', label: 'Created date' },
  { id: 'updated', label: 'Updated date' },
  { id: 'priority', label: 'Priority' },
  { id: 'name', label: 'Name' },
];

export function Views() {
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>({});
  const [selectedGroupBy, setSelectedGroupBy] = useState('state');
  const [selectedSortBy, setSelectedSortBy] = useState('created');

  const { data: views, isLoading } = useQuery({
    queryKey: ['views', projectId],
    queryFn: () => api.getViews(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const { data: cycles } = useQuery({
    queryKey: ['cycles', projectId],
    queryFn: () => api.getCycles(projectId!),
    enabled: !!projectId,
  });

  const createViewMutation = useMutation({
    mutationFn: (data: any) => api.createView(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['views', projectId] });
      setIsCreating(false);
      setNewViewName('');
      setSelectedFilters({});
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: (id: string) => api.deleteView(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['views', projectId] });
    },
  });

  const handleCreateView = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newViewName.trim()) return;
    createViewMutation.mutate({
      bh_name: newViewName.trim(),
      bh_filters: selectedFilters,
      bh_display: { groupBy: selectedGroupBy, sortBy: selectedSortBy },
      bh_project: projectId,
    });
  };

  const getFilterDescription = (view: any) => {
    const filters = view.bh_filters || {};
    const parts: string[] = [];

    if (filters.assignee) {
      const member = members?.find((m: any) => m.bh_memberid === filters.assignee);
      parts.push(`Assignee: ${member?.bh_name || 'Unknown'}`);
    }
    if (filters.priority?.length) {
      parts.push(`Priority: ${filters.priority.map((p: number) => ['None', 'Low', 'Medium', 'High', 'Urgent'][p]).join(', ')}`);
    }
    if (filters.state) {
      const state = states?.find((s: any) => s.bh_stateid === filters.state);
      parts.push(`State: ${state?.bh_name || 'Unknown'}`);
    }
    if (filters.cycle) {
      const cycle = cycles?.find((c: any) => c.bh_cycleid === filters.cycle);
      parts.push(`Cycle: ${cycle?.bh_name || 'Unknown'}`);
    }

    return parts.length > 0 ? parts.join(' • ') : 'No filters';
  };

  const applyView = (view: any) => {
    // Navigate to list view with filters encoded in URL
    const params = new URLSearchParams();
    if (view.bh_filters) {
      params.set('filters', JSON.stringify(view.bh_filters));
    }
    if (view.bh_display) {
      params.set('display', JSON.stringify(view.bh_display));
    }
    navigate(`/workspaces/${workspaceId}/projects/${projectId}/list?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <LayoutGrid className="w-8 h-8 text-primary-600" />
            <h1 className="text-2xl font-bold text-gray-900">Saved Views</h1>
          </div>
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Create View
          </button>
        </div>

        {/* Create View Form */}
        {isCreating && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New View</h3>
            <form onSubmit={handleCreateView}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">View Name</label>
                  <input
                    type="text"
                    value={newViewName}
                    onChange={(e) => setNewViewName(e.target.value)}
                    placeholder="e.g., My Tasks, High Priority"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Filters */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Filter className="w-4 h-4 inline mr-1" />
                      Filters
                    </label>
                    <div className="space-y-2">
                      <select
                        value={selectedFilters.assignee || ''}
                        onChange={(e) => setSelectedFilters(prev => ({
                          ...prev,
                          assignee: e.target.value || undefined,
                        }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Any assignee</option>
                        {members?.map((m: any) => (
                          <option key={m.bh_memberid} value={m.bh_memberid}>{m.bh_name}</option>
                        ))}
                      </select>

                      <select
                        value={selectedFilters.state || ''}
                        onChange={(e) => setSelectedFilters(prev => ({
                          ...prev,
                          state: e.target.value || undefined,
                        }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Any state</option>
                        {states?.map((s: any) => (
                          <option key={s.bh_stateid} value={s.bh_stateid}>{s.bh_name}</option>
                        ))}
                      </select>

                      <select
                        value={selectedFilters.cycle || ''}
                        onChange={(e) => setSelectedFilters(prev => ({
                          ...prev,
                          cycle: e.target.value || undefined,
                        }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Any cycle</option>
                        {cycles?.map((c: any) => (
                          <option key={c.bh_cycleid} value={c.bh_cycleid}>{c.bh_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Display Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <SortAsc className="w-4 h-4 inline mr-1" />
                      Display
                    </label>
                    <div className="space-y-2">
                      <select
                        value={selectedGroupBy}
                        onChange={(e) => setSelectedGroupBy(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {GROUP_OPTIONS.map(opt => (
                          <option key={opt.id} value={opt.id}>Group by: {opt.label}</option>
                        ))}
                      </select>

                      <select
                        value={selectedSortBy}
                        onChange={(e) => setSelectedSortBy(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {SORT_OPTIONS.map(opt => (
                          <option key={opt.id} value={opt.id}>Sort by: {opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={createViewMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    Create View
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreating(false);
                      setNewViewName('');
                      setSelectedFilters({});
                    }}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Views List */}
        {views?.length === 0 && !isCreating ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <LayoutGrid className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No saved views yet</h3>
            <p className="text-gray-500 mb-4">Create custom views to filter and organize your issues</p>
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" />
              Create Your First View
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {views?.map((view: any) => (
              <div
                key={view.bh_viewid}
                className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:border-primary-200 transition-colors group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <List className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{view.bh_name}</h3>
                      <p className="text-xs text-gray-500">
                        Created {new Date(view.createdon).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm('Delete this view?')) {
                        deleteViewMutation.mutate(view.bh_viewid);
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="text-sm text-gray-600 mb-4 flex items-center gap-1">
                  <Filter className="w-3.5 h-3.5" />
                  {getFilterDescription(view)}
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                  <span className="px-2 py-1 bg-gray-100 rounded">
                    Group: {view.bh_display?.groupBy || 'state'}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 rounded">
                    Sort: {view.bh_display?.sortBy || 'created'}
                  </span>
                </div>

                <button
                  onClick={() => applyView(view)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Apply View
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
